import { Outlet } from "react-router-dom";
import React, { useState, useEffect } from "react";
import MyQRCodes from "./MyQRCodes";
import AdvancedGenerator from "./AdvancedGenerator";
import QRDetails from "./QRDetails";
import BulkGenerator from "./pages/BulkGenerator";
import { supabase } from "./lib/supabaseClient";
import { useToast } from "./ToastContext";
import LeadsList from "./pages/LeadsList";
import LeadsInbox from "./pages/LeadsInbox";
import FeedbackInbox from "./pages/FeedbackInbox";
import AnalyticsDashboard from "./pages/AnalyticsDashboard";
import { useNavigate, useLocation, Routes, Route } from "react-router-dom";

export default function Layout({ user, isAdmin, isOnTrial, trialDaysLeft }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [editingQR, setEditingQR] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [refreshKey, setRefreshKey] = useState(Date.now());
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToast();

  useEffect(() => {
    const fetchTodayFeedbackCount = async () => {
      try {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const { count, error } = await supabase
          .from("feedback")
          .select("*", { count: 'exact', head: true })
          .gte("created_at", today.toISOString());
        if (!error) setUnreadCount(count || 0);
      } catch (err) { console.error(err); }
    };
    fetchTodayFeedbackCount();

    const channel = supabase.channel('schema-db-changes')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'qr_logs' },
        () => {
          addToast(`New Scan!`, 'success');

          // ✅ THIS IS THE REAL FIX
          setRefreshKey(Date.now());
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [addToast]);

  const handleSaveSuccess = () => {
    setEditingQR(null);
    setRefreshKey(Date.now());
    navigate("/dashboard");
    setSidebarOpen(false);
  };

  const navigateToList = () => {
    setRefreshKey(Date.now());
    setEditingQR(null);
    navigate("/dashboard");
    setSidebarOpen(false);
  }; // ✅ FIX

  return (
    <div className="flex h-screen bg-slate-950 text-white relative overflow-hidden font-sans">

      {/* SIDEBAR */}
      <div className={`
        fixed md:relative top-0 left-0 h-full w-64 
        bg-black/40 backdrop-blur-3xl border-r border-white/5 flex flex-col z-50
        transform transition-transform duration-300
        ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
        md:translate-x-0
      `}>

        {/* LOGO */}
        <div className="p-6 flex items-center gap-3">
          <div className="bg-white p-1 rounded-md flex items-center justify-center">
            <img
              src="/Digitoly Logo transparent.png"
              alt="Digitoly Logo"
              className="h-8 w-auto"
            />
          </div>
        </div>

        {/* NAV */}
        <nav className="px-4 flex-1 space-y-1 overflow-y-auto custom-scrollbar">
          <div className="pb-4">
            <p className="px-3 text-[10px] font-black text-white uppercase tracking-[5px] mb-2">Main Menu</p>

            <button
              onClick={() => navigate("/dashboard")}
              className={`w-full text-left px-3 py-2.5 rounded-xl transition-all flex items-center gap-3 ${location.pathname === "/dashboard"
                ? "bg-red-600 text-white shadow-lg"
                : "text-gray-500 hover:bg-white/5"
                }`}
            >
              <span className="text-sm">📋</span>
              <span className="text-[10px] font-black uppercase tracking-widest">My Assets</span>
            </button>

            <button onClick={() => {
              if (location.pathname.startsWith("/dashboard/create") || location.pathname.startsWith("/dashboard/edit")) {
                window.location.href = "/dashboard/create";
              } else {
                navigate("/dashboard/create");
              }
              setSidebarOpen(false);
            }}
              className={`w-full text-left px-3 py-2.5 rounded-xl transition-all flex items-center gap-3 mt-1 ${location.pathname.startsWith("/dashboard/create") ? "bg-red-600 text-white shadow-lg" : "text-gray-500 hover:bg-white/5"}`}>
              <span className="text-sm">➕</span>
              <span className="text-[10px] font-black uppercase tracking-widest">Create New</span>
            </button>

            <button
              onClick={() => {
                navigate("/dashboard/bulk");
                setEditingQR(null);
                setSidebarOpen(false);
              }}
              className={`w-full text-left px-3 py-2.5 rounded-xl transition-all flex items-center gap-3 mt-1 ${location.pathname.startsWith("/dashboard/bulk")
                ? "bg-red-600 text-white shadow-lg"
                : "text-gray-500 hover:bg-white/5"
                }`}
            >
              <span className="text-sm">📦</span>
              <span className="text-[10px] font-black uppercase tracking-widest">
                Bulk Engine
              </span>
            </button>
          </div>

          <div className="pt-4 border-t border-white/5">
            <p className="px-3 text-[10px] font-black text-white uppercase tracking-[5px] mb-2">Analytics</p>

            <button onClick={() => { navigate("/dashboard/analytics"); setSidebarOpen(false); }}
              className={`w-full text-left px-3 py-2.5 rounded-xl transition-all flex items-center gap-3 ${location.pathname.startsWith("/dashboard/analytics") ? "bg-red-600 text-white shadow-lg" : "text-gray-500 hover:bg-white/5"}`}>
              <span className="text-sm">📊</span>
              <span className="text-[10px] font-black uppercase tracking-widest">Powerhouse</span>
            </button>

            <button onClick={() => { navigate("/dashboard/leads"); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all mt-1 ${location.pathname.startsWith("/dashboard/leads") ? "bg-red-600 text-white" : "text-slate-500 hover:bg-white/5"}`}>
              <span className="text-sm">👤</span>
              <span className="text-[10px] font-black uppercase tracking-widest">Leads Inbox</span>
            </button>

            <button onClick={() => { navigate("/dashboard/feedback"); setSidebarOpen(false); }}
              className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl transition-all mt-1 ${location.pathname.startsWith("/dashboard/feedback") ? "bg-red-600 text-white" : "text-slate-500 hover:bg-white/5"}`}>
              <div className="flex items-center gap-3">
                <span className="text-sm">📥</span>
                <span className="text-[10px] font-black uppercase tracking-widest">Feedback Inbox</span>
              </div>
              {unreadCount > 0 && <span className="bg-white text-red-600 text-[8px] px-1.5 py-0.5 rounded-md font-black">{unreadCount}</span>}
            </button>
            <button onClick={() => { navigate("/dashboard/notifications"); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all mt-1 ${location.pathname.startsWith("/dashboard/notifications") || location.pathname.startsWith("/dashboard/settings") ? "bg-red-600 text-white" : "text-slate-500 hover:bg-white/5"}`}>
              <span className="text-sm">🔔</span>
              <span className="text-[10px] font-black uppercase tracking-widest">Notifications</span>
            </button>
            <button onClick={() => { navigate("/dashboard/team"); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all mt-1 ${location.pathname.startsWith("/dashboard/team") ? "bg-red-600 text-white" : "text-slate-500 hover:bg-white/5"}`}>
              <span className="text-sm">👥</span>
              <span className="text-[10px] font-black uppercase tracking-widest">Team</span>
            </button>

            <button onClick={() => { navigate("/dashboard/coupons"); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all mt-1 ${location.pathname.startsWith("/dashboard/coupons") ? "bg-red-600 text-white" : "text-slate-500 hover:bg-white/5"}`}>
              <span className="text-sm">🎟️</span>
              <span className="text-[10px] font-black uppercase tracking-widest">Coupons</span>
            </button>
            <button onClick={() => { navigate("/dashboard/menu-builder"); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all mt-1 ${location.pathname.startsWith("/dashboard/menu-builder") ? "bg-red-600 text-white" : "text-slate-500 hover:bg-white/5"}`}>
              <span className="text-sm">🍽️</span>
              <span className="text-[10px] font-black uppercase tracking-widest">Menu Builder</span>
            </button>

            <button onClick={() => { navigate("/dashboard/health"); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all mt-1 ${location.pathname.startsWith("/dashboard/health") ? "bg-red-600 text-white" : "text-slate-500 hover:bg-white/5"}`}>
              <span className="text-sm">🔍</span>
              <span className="text-[10px] font-black uppercase tracking-widest">Health Monitor</span>
            </button>

            {isAdmin && (
              <button
                onClick={() => { navigate("/admin"); setSidebarOpen(false); }}
                className={`w-full text-left px-3 py-2.5 rounded-xl transition-all flex items-center gap-3 mt-1 ${location.pathname === "/admin" ? "bg-red-600 text-white shadow-lg" : "text-gray-500 hover:bg-white/5"
                  }`}
              >
                <span className="text-sm">🔐</span>
                <span className="text-[10px] font-black uppercase tracking-widest">Admin</span>
              </button>
            )}
          </div>
        </nav>

        {/* FOOTER */}
        <div className="p-4 bg-black/20 border-t border-white/5">
          <div className="flex flex-col gap-1 mb-4">
            <a href="/privacy" target="_blank" className="px-3 text-[8px] text-gray-600 hover:text-red-500 font-black uppercase tracking-widest transition-colors">Privacy Policy</a>
            <a href="/terms" target="_blank" className="px-3 text-[8px] text-gray-600 hover:text-red-500 font-black uppercase tracking-widest transition-colors">Terms of Service</a>
            <a href="/help" target="_blank" className="px-3 text-[8px] text-gray-600 hover:text-blue-500 font-black uppercase tracking-widest transition-colors">Help Center</a>
          </div>
          <div className="flex items-center gap-3 mb-3 px-2">
            <div className="w-7 h-7 rounded-lg bg-slate-800 flex items-center justify-center border border-white/10 text-[10px] font-black text-red-500">
              {user?.email?.[0]?.toUpperCase() || user?.user_metadata?.full_name?.[0]?.toUpperCase() || "D"}
            </div>
            <p className="text-[8px] font-black text-white/50 uppercase tracking-tighter truncate w-32">
              {user?.email || user?.user_metadata?.full_name || "Facebook User"}
            </p>
          </div>

          <button
            onClick={async () => { await supabase.auth.signOut(); window.location.href = "/"; }}
            className="w-full py-2.5 rounded-xl bg-red-600/10 hover:bg-red-600 text-red-500 hover:text-white border border-red-500/20 transition-all font-black text-[9px] uppercase tracking-widest">
            Logout
          </button>
        </div>
      </div>

      {/* ✅ OVERLAY (FIXED POSITION) */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* MAIN */}
      <div className="flex-1 p-8 overflow-auto relative z-10 bg-slate-950">

        {/* HAMBURGER */}
        <div className="md:hidden mb-4">
          <button
            onClick={() => setSidebarOpen(true)}
            className="text-white bg-white/5 hover:bg-white/10 p-2 rounded-lg transition"
          >
            ☰
          </button>
        </div>
        {isOnTrial && trialDaysLeft !== null && (
          <div className="mb-6 bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-blue-500/30 rounded-2xl px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-xl">🎁</span>
              <div>
                <p className="text-white text-[11px] font-black uppercase tracking-wide">
                  Free Trial — {trialDaysLeft} day{trialDaysLeft !== 1 ? "s" : ""} remaining
                </p>
                <p className="text-gray-400 text-[10px]">Full Agency access · Upgrade before trial ends to keep all features</p>
              </div>
            </div>
            <button
              onClick={() => navigate("/dashboard/create")}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-xl text-[10px] font-black uppercase text-white transition-all shrink-0"
            >
              Upgrade Now
            </button>
          </div>
        )}
        <div className="max-w-5xl mx-auto">
          <Outlet />
        </div>
      </div>
    </div>
  );
}